import { useState, useEffect, useRef, useCallback, useId } from 'react'
import { useTranslation } from 'react-i18next'
import './AirportInput.css'

function AirportInput({ label, placeholder, value, selectedItem, onChange, onSelect, name }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const wrapperRef = useRef(null)
  const debounceRef = useRef(null)
  const lastLoadedRef = useRef('')
  const listboxId = useId()

  const fetchAirports = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setOptions([])
      return
    }
    setLoading(true)
    try {
      const res = await fetch(`/api/airport/filter?searchStr=${encodeURIComponent(q)}`)
      if (!res.ok) throw new Error('Network error')
      const data = await res.json()
      setOptions(data || [])
      setActiveIndex(-1)
    } catch (err) {
      setOptions([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (selectedItem) {
      const displayCity = selectedItem.City || selectedItem.Name || selectedItem.Code
      setQuery(`${displayCity} (${selectedItem.Code})`)
      lastLoadedRef.current = selectedItem.Code
      return
    }

    if (!value) {
      setQuery('')
      lastLoadedRef.current = ''
      return
    }

    if (value === lastLoadedRef.current) return

    fetch(`/api/airport/filter?searchStr=${encodeURIComponent(value)}&byCode=true`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const item = Array.isArray(data) ? data[0] : data
        if (item) {
          setQuery(`${item.City} (${item.Code})`)
          lastLoadedRef.current = item.Code
        } else {
          setQuery(value)
          lastLoadedRef.current = value
        }
      })
      .catch(() => {
        setQuery(value)
        lastLoadedRef.current = value
      })
  }, [value, selectedItem])

  const handleInputChange = (e) => {
    const val = e.target.value
    setQuery(val)
    lastLoadedRef.current = ''
    onChange?.({ target: { name, value: '' } })
    setIsOpen(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchAirports(val), 200)
  }

  const handleSelect = (item) => {
    setQuery(`${item.City} (${item.Code})`)
    lastLoadedRef.current = item.Code
    setIsOpen(false)
    setActiveIndex(-1)
    onSelect?.(item)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && isOpen) {
      e.preventDefault()
      setIsOpen(false)
      setActiveIndex(-1)
      return
    }

    if (loading || options.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setIsOpen(true)
      setActiveIndex((current) => Math.min(current + 1, options.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setIsOpen(true)
      setActiveIndex((current) => current <= 0 ? options.length - 1 : current - 1)
    } else if (e.key === 'Enter' && isOpen && activeIndex >= 0) {
      e.preventDefault()
      handleSelect(options[activeIndex])
    }
  }

  useEffect(() => {
    if (activeIndex < 0) return
    document.getElementById(`${listboxId}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, listboxId])

  return (
    <div className="airport-input-wrapper" ref={wrapperRef}>
      <input
        className="finput"
        type="text"
        placeholder={placeholder}
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => query.length >= 2 && setIsOpen(true)}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
        autoComplete="off"
      />
      {isOpen && (
        <div className="airport-dropdown" id={listboxId} role="listbox">
          {loading && <div className="airport-dropdown-item">{t('airport.loading')}</div>}
          {!loading && options.length === 0 && query.length >= 2 && (
            <div className="airport-dropdown-item">{t('airport.noResults')}</div>
          )}
          {!loading && options.map((item, idx) => (
            <div
              key={`${item.Code || item.IataCode || 'airport'}-${item.City || ''}-${item.Name || ''}-${idx}`}
              id={`${listboxId}-option-${idx}`}
              className={`airport-dropdown-item${activeIndex === idx ? ' active' : ''}`}
              role="option"
              aria-selected={activeIndex === idx}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(item)}
            >
              <div className="airport-flag">
                {item.ImageData && (
                  <img src={`data:image/${item.ImageType};base64,${item.ImageData}`} alt="" />
                )}
              </div>
              <div className="airport-text">
                {item.City} - {item.Name} ({item.Code})
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AirportInput
